import { useState, useEffect, useCallback } from 'react'

interface TableSettings {
    fontSize?: string
    rowHeight?: string
    hiddenColumns?: string[]
    columnsPinned?: boolean
    filterToggles?: Record<string, boolean>
    hiddenOptions?: Record<string, string[]>
}

/**
 * Hook to read/sync table settings from localStorage
 * Used by FilterBar.Item to auto-hide based on toggle state
 *
 * @param storageKey - Same key used in DataTableCustomized toolbar.settings.storageKey
 * @returns Object with settings and helper functions
 *
 * @example
 * const { isToggleEnabled } = useTableSettings('dayoff-list')
 * if (isToggleEnabled('showEmployeeFilter')) {
 *   // render employee filter
 * }
 */
export function useTableSettings(storageKey?: string) {
    const [settings, setSettings] = useState<TableSettings>(() => {
        if (!storageKey) return {}
        try {
            const stored = localStorage.getItem(`dt-settings-${storageKey}`)
            if (stored) return JSON.parse(stored)
        } catch {
            // Ignore parse errors
        }
        return {}
    })

    // Listen for storage changes (from other tabs or DataTableCustomized updates)
    useEffect(() => {
        if (!storageKey) return

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === `dt-settings-${storageKey}` && e.newValue) {
                try {
                    setSettings(JSON.parse(e.newValue))
                } catch {
                    // Ignore parse errors
                }
            }
        }

        // Also poll for changes since storage events don't fire in same tab
        const interval = setInterval(() => {
            try {
                const stored = localStorage.getItem(`dt-settings-${storageKey}`)
                if (stored) {
                    const parsed = JSON.parse(stored)
                    setSettings((prev) => {
                        // Only update if changed
                        if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
                            return parsed
                        }
                        return prev
                    })
                }
            } catch {
                // Ignore
            }
        }, 500)

        window.addEventListener('storage', handleStorageChange)
        return () => {
            window.removeEventListener('storage', handleStorageChange)
            clearInterval(interval)
        }
    }, [storageKey])

    /**
     * Check if a filter toggle is enabled
     * Returns true by default if toggle doesn't exist (show filter by default)
     */
    const isToggleEnabled = useCallback(
        (key: string, defaultValue = true) => {
            return settings.filterToggles?.[key] ?? defaultValue
        },
        [settings.filterToggles]
    )

    /**
     * Get hidden options for a specific filter key
     * Returns empty array if no hidden options configured
     */
    const getHiddenOptions = useCallback(
        (key: string): string[] => {
            return settings.hiddenOptions?.[key] ?? []
        },
        [settings.hiddenOptions]
    )

    /**
     * Filter options array by removing hidden options
     */
    const filterOptions = useCallback(
        <T extends { value: string }>(key: string, options: T[]): T[] => {
            const hidden = settings.hiddenOptions?.[key] ?? []
            if (hidden.length === 0) return options
            return options.filter((opt) => !hidden.includes(opt.value))
        },
        [settings.hiddenOptions]
    )

    return {
        settings,
        filterToggles: settings.filterToggles || {},
        hiddenOptions: settings.hiddenOptions || {},
        isToggleEnabled,
        getHiddenOptions,
        filterOptions,
    }
}
